import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-6ff8a3bc")
const _hoisted_1 = { class: "drawflow-container" }
const _hoisted_2 = {
  key: 1,
  class: "shadow pa-4 widget-list card"
}
const _hoisted_3 = {
  key: 2,
  class: "shadow pa-4 details card"
}
_popScopeId()

import { h, getCurrentInstance, render, ref, onMounted, computed } from 'vue'
import createHoppermationAPIClient from '@/clients/hoppermation'
import bus from '@/lib/eventBus'
import EventNames from '@/lib/enums/eventNames'
import Drawflow from 'drawflow'
import stepTemplates from '../components/Editor/Step'
import Step from '../components/Editor/Step/Step.vue'
import AppBar from '@/components/Editor/AppBar.vue'
import WidgetList from '../components/Editor/WidgetList.vue'
import BaseSnackbar from '@/components/BaseSnackbar.vue'
import Details from '@/components/Editor/Details/Details.vue'
import StepType from '@/lib/enums/StepType'
import AutomationState from '@/lib/enums/AutomationState'
import { DfMobileLastMove, DfNode } from '@/interfaces/drawflow'
import { prepareDataForImport, prepareDataForExport } from '@/lib/drawflow/mapper'
import { AxiosInstance } from 'axios'
import { useRoute } from 'vue-router'
import { IAutomation } from '@/interfaces/hoppermation'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const route = useRoute()
const Vue = { version: 3, h, render }
const internalInstance = getCurrentInstance()

const isDataReady = ref(false)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const editor = ref({} as any)
const selectedNode = ref({} as DfNode)
const selectedNodeIds = ref([] as number[])
const copiedNodes = ref([] as DfNode[])
const pastedCount = ref(1)
const showNodeDetails = ref(false)
const automation = ref({} as IAutomation)
const hoppermationApiClient = ref({} as AxiosInstance)
const mobile_item_selec = ref(StepType.TRIGGER) // mobile support
const mobile_last_move = ref({} as DfMobileLastMove) // mobile support

const isActive = computed((): boolean => {
  if (automation.value && automation.value.status && automation.value.status === AutomationState.ACTIVE) {
    return true
  } else {
    return false
  }
})

onMounted(async () => {
  hoppermationApiClient.value = await createHoppermationAPIClient()

  // get automation data and setup the editor
  try {
    const res = await hoppermationApiClient.value.get(`/automations/${route.params.id}`)
    automation.value = res.data
    isDataReady.value = true
    setupEditor()
    importData()
  } catch (error) {
    bus.$emit(EventNames.SHOW_SNACKBAR, { color: 'error', text: 'snackbar.automation.import.error' })
  }
})

const importData = async (): Promise<void> => {
  const res = await hoppermationApiClient.value.get(`/automations/${automation.value.id}/ui-steps`)
  const { data: triggerEvents } = await hoppermationApiClient.value.get('/trigger-events')
  const importedData = prepareDataForImport(res.data, triggerEvents)
  editor.value.import(importedData)
  //straighten the connection lines after import
  setTimeout(() => {
    Object.keys(importedData.drawflow.Home.data).forEach((key) => {
      editor.value.updateConnectionNodes(`node-${key}`)
    })
  }, 10)
}

const save = async (): Promise<void> => {
  const exportData = prepareDataForExport(editor.value.export().drawflow.Home.data)

  // Update steps of the automation
  try {
    await hoppermationApiClient.value.post(`/automations/${automation.value.id}/ui-steps`, exportData)
    bus.$emit(EventNames.SHOW_SNACKBAR, { color: 'success', text: 'snackbar.automation.save.success' })
  } catch (error) {
    bus.$emit(EventNames.SHOW_SNACKBAR, { color: 'error', text: 'snackbar.automation.save.error' })
  }
}

const setupEditor = (): void => {
  // Setup up the editor
  const drawflowElement = document.getElementById('drawflow')
  editor.value = new Drawflow(drawflowElement, Vue, internalInstance?.appContext.app._context)

  // Lock if not draft state
  if (automation.value.status !== AutomationState.DRAFT) {
    editor.value.editor_mode = 'fixed'
  }
  // Enable moving nodes, when in fixed mode
  editor.value.on('click', (e: Event) => {
    const target = e.target as HTMLElement
    if (automation.value.status !== AutomationState.DRAFT) {
      if (target.closest('.drawflow_content_node') != null) {
        editor.value.ele_selected = target.closest('.drawflow_content_node')?.parentElement
        if (editor.value.ele_selected.classList[0] === 'drawflow-node') {
          editor.value.editor_mode = 'edit'
        }
      }
    }
  })

  // Enable external editor
  editor.value.on('nodeSelected', (id: string) => {
    selectedNode.value = editor.value.getNodeFromId(id)
  })
  editor.value.on('nodeUnselected', () => {
    cancelNodeDetails()
  })

  // Enforce subscribe after trigger step
  editor.value.on(
    'connectionCreated',
    async ({
      output_id,
      input_id
    }: {
      output_id: string
      input_id: string
      output_class: string
      input_class: string
    }) => {
      const outputNode = await editor.value.getNodeFromId(output_id)
      if (outputNode.name === StepType.TRIGGER) {
        const inputNode = await editor.value.getNodeFromId(input_id)
        if (inputNode.name !== StepType.SUBSCRIBE) {
          bus.$emit(EventNames.SHOW_SNACKBAR, {
            color: 'warning',
            text: 'snackbar.automation.connection.warning',
            time: 10000
          })
        }
      }
    }
  )

  // only one connection
  editor.value.on(
    'connectionCreated',
    async ({
      output_id,
      input_id,
      output_class,
      input_class
    }: {
      output_id: string
      input_id: string
      output_class: string
      input_class: string
    }) => {
      const outputNode = await editor.value.getNodeFromId(output_id)
      if (
        outputNode.outputs.output_1.connections.length > 1 ||
        outputNode.outputs.output_2?.connections.length > 1 ||
        outputNode.outputs.output_3?.connections.length > 1
      ) {
        editor.value.removeSingleConnection(output_id, input_id, output_class, input_class)
        bus.$emit(EventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: 'snackbar.automation.singleConnection.error',
          time: 4000
        })
      }
    }
  )

  // Make lines straight
  editor.value.curvature = 0
  editor.value.reroute_curvature_start_end = 0
  editor.value.reroute_curvature = 0
  editor.value.createCurvature = function (
    start_pos_x: number,
    start_pos_y: number,
    end_pos_x: number,
    end_pos_y: number
  ) {
    var center_y = (end_pos_y - start_pos_y) / 2 + start_pos_y
    return (
      ' M ' +
      start_pos_x +
      ' ' +
      start_pos_y +
      ' L ' +
      start_pos_x +
      ' ' +
      center_y +
      ' L ' +
      end_pos_x +
      ' ' +
      center_y +
      ' L ' +
      end_pos_x +
      ' ' +
      end_pos_y
    )
  }

  editor.value.on('keydown', ({ key, metaKey }: { key: string; metaKey: boolean }) => {
    if (key === 'c' && metaKey) {
      if (selectedNodeIds.value?.length > 0) {
        copiedNodes.value = selectedNodeIds.value.map((id) => editor.value.getNodeFromId(id))
      } else {
        copiedNodes.value = [selectedNode.value]
      }
      pastedCount.value = 1
    } else if (key === 'v' && metaKey) {
      const copies = copiedNodes.value.map(({ id, name, pos_x, pos_y, data, inputs }) => {
        const copyId = editor.value.addNode(
          name,
          stepTemplates[name].inputs,
          stepTemplates[name].outputs,
          pos_x + 10 * pastedCount.value,
          pos_y + 10 * pastedCount.value,
          name,
          data,
          name,
          'vue'
        )
        return { id, copyId, inputs }
      })
      const idMapping: Record<string, number> = copies.reduce((obj, { id, copyId }) => ({ ...obj, [id]: copyId }), {})
      copies.forEach(({ copyId, inputs }) => {
        inputs?.input_1?.connections?.forEach(({ node: outputId, input: outputClass }) => {
          editor.value.addConnection(idMapping[outputId], copyId, outputClass, 'input_1')
        })
      })
      if (selectedNodeIds.value?.length > 0) {
        selectedNodeIds.value = copies.map(({ copyId }) => copyId)
      }
      pastedCount.value = pastedCount.value + 1
    }
  })

  // Snap Nodes to Grid
  const gridSize = 10
  editor.value.on('nodeMoved', (id: string) => {
    const module = editor.value.getModuleFromNodeId(id)
    const data = editor.value.getNodeFromId(id)
    const pos_x = data.pos_x
    const pos_y = data.pos_y

    const snapedPosX = Math.round(pos_x / gridSize) * gridSize
    const snapedPosY = Math.round(pos_y / gridSize) * gridSize

    editor.value.drawflow.drawflow[module].data[id].pos_x = snapedPosX
    editor.value.drawflow.drawflow[module].data[id].pos_y = snapedPosY
    const node = document.getElementById(`node-${id}`)
    if (node) {
      node.style.left = snapedPosX + 'px'
      node.style.top = snapedPosY + 'px'
    }
    editor.value.updateConnectionNodes(`node-${id}`)
  })

  editor.value.on('clickEnd', ({ shiftKey }: { shiftKey: boolean }) => {
    if (automation.value.status !== AutomationState.DRAFT) {
      editor.value.editor_mode = 'fixed'
    }
    if (shiftKey) {
      if (editor.value.node_selected !== null) {
        const nodeId = editor.value.node_selected.id.slice(5)
        const indexSelected = selectedNodeIds.value.indexOf(nodeId)
        if (indexSelected === -1) {
          selectedNodeIds.value.push(nodeId)
        } else {
          selectedNodeIds.value.splice(indexSelected, 1)
          const nodeUnselected = document.getElementById(`node-${nodeId}`)
          nodeUnselected?.classList?.remove('selected')
        }
      } else {
        selectedNodeIds.value.forEach((id) => {
          const node = document.getElementById(`node-${id}`)
          node?.classList?.remove('selected')
        })
        selectedNodeIds.value.splice(0, selectedNodeIds.value.length)
      }
      selectedNodeIds.value.forEach((id) => {
        const node = document.getElementById(`node-${id}`)
        node?.classList?.add('selected')
      })
    } else {
      selectedNodeIds.value.forEach((id) => {
        const node = document.getElementById(`node-${id}`)
        node?.classList?.remove('selected')
      })
      selectedNodeIds.value.splice(0, selectedNodeIds.value.length)
    }
  })

  let last_x = 0
  let last_y = 0
  editor.value.on('mouseMove', ({ x, y }: { x: number; y: number }) => {
    if (editor.value.node_selected && editor.value.drag) {
      editor.value.node_selected.classList.add('selected')
      const nodeId = editor.value.node_selected.id.slice(5)
      if (!selectedNodeIds.value.includes(nodeId)) {
        selectedNodeIds.value.push(nodeId)
      }
      selectedNodeIds.value.forEach((id) => {
        if (id != editor.value.node_selected.id.slice(5)) {
          const node = document.getElementById(`node-${id}`)
          if (node) {
            var xnew =
              ((last_x - x) * editor.value.precanvas.clientWidth) /
              (editor.value.precanvas.clientWidth * editor.value.zoom)
            var ynew =
              ((last_y - y) * editor.value.precanvas.clientHeight) /
              (editor.value.precanvas.clientHeight * editor.value.zoom)

            node.style.top = node.offsetTop - ynew + 'px'
            node.style.left = node.offsetLeft - xnew + 'px'

            editor.value.drawflow.drawflow[editor.value.module].data[id].pos_x = node.offsetLeft - xnew
            editor.value.drawflow.drawflow[editor.value.module].data[id].pos_y = node.offsetTop - ynew
            editor.value.updateConnectionNodes(`node-${id}`)
          }
        }
      })
    }
    last_x = x
    last_y = y
  })
  editor.value.start()

  // Register nodes
  const nodeProps = (key: StepType) => ({
    step: stepTemplates[key],
    toggleDetails: toggleNodeDetails,
    editor: editor.value
  })

  ;(Object.keys(stepTemplates) as StepType[]).forEach((value: StepType) => {
    editor.value.registerNode(value, Step, nodeProps(value))
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setNodeDetails = async (id: number, details: any): Promise<void> => {
  await editor.value.updateNodeDataFromId(id, details)
  bus.$emit(EventNames.DF_UPDATE_NODE_DETAILS, { id: `node-${id}`, details })
  cancelNodeDetails()
}

const toggleNodeDetails = (): void => {
  showNodeDetails.value = true
}

const cancelNodeDetails = (): void => {
  if (editor.value.node_selected) {
    editor.value.node_selected.classList.remove('selected')
    editor.value.node_selected = null
  }
  showNodeDetails.value = false
  selectedNode.value = {} as DfNode
}

const zoom = (direction: string): void => {
  direction === 'in' ? editor.value.zoom_in() : editor.value.zoom_out()
}

const drag = (ev: DragEvent): void => {
  const target = (ev && ev.target) as Element
  if (ev.type === 'touchstart') {
    mobile_item_selec.value = target?.closest('.drag-drawflow')?.getAttribute('data-node') as StepType
  } else {
    ev?.dataTransfer?.setData('node', target?.getAttribute('data-node') || '')
  }
}

const drop = (ev: DragEvent): void => {
  if (ev.type === 'touchend') {
    if (mobile_last_move.value && mobile_last_move.value.touches) {
      var parentdrawflow = document
        .elementFromPoint(mobile_last_move.value.touches[0].clientX, mobile_last_move.value.touches[0].clientY)
        ?.closest('#drawflow')
      if (parentdrawflow != null) {
        addNodeToDrawFlow(
          mobile_item_selec.value,
          mobile_last_move.value.touches[0].clientX,
          mobile_last_move.value.touches[0].clientY
        )
      }
      mobile_item_selec.value = StepType.TRIGGER
    }
  } else {
    ev.preventDefault()
    if (ev && ev.dataTransfer) {
      var data = ev.dataTransfer.getData('node') as StepType
      addNodeToDrawFlow(data, ev.clientX, ev.clientY)
    }
  }
}

const addNodeToDrawFlow = (name: StepType, pos_x: number, pos_y: number): void => {
  if (editor.value.editor_mode === 'fixed') {
    return
  }
  pos_x =
    pos_x * (editor.value.precanvas.clientWidth / (editor.value.precanvas.clientWidth * editor.value.zoom)) -
    editor.value.precanvas.getBoundingClientRect().x *
      (editor.value.precanvas.clientWidth / (editor.value.precanvas.clientWidth * editor.value.zoom))
  pos_y =
    pos_y * (editor.value.precanvas.clientHeight / (editor.value.precanvas.clientHeight * editor.value.zoom)) -
    editor.value.precanvas.getBoundingClientRect().y *
      (editor.value.precanvas.clientHeight / (editor.value.precanvas.clientHeight * editor.value.zoom))

  editor.value.addNode(
    name,
    stepTemplates[name].inputs,
    stepTemplates[name].outputs,
    pos_x,
    pos_y,
    name,
    stepTemplates[name].data,
    name,
    'vue'
  )
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      id: "drawflow",
      onDrop: _cache[0] || (_cache[0] = ($event: any) => (drop($event))),
      onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
      onDragenter: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
    }, null, 32),
    (isDataReady.value)
      ? (_openBlock(), _createBlock(AppBar, {
          key: 0,
          class: "px-5 mt-0",
          zoom: zoom,
          save: save,
          automation: automation.value
        }, null, 8, ["automation"]))
      : _createCommentVNode("", true),
    (isDataReady.value && !_unref(isActive))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(WidgetList, { drag: drag })
        ]))
      : _createCommentVNode("", true),
    (showNodeDetails.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(Details, {
            node: selectedNode.value,
            submit: setNodeDetails,
            cancel: cancelNodeDetails
          }, null, 8, ["node"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(BaseSnackbar)
  ]))
}
}

})